import { useEffect, useMemo, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open overlays, oldest first. Only the last one answers keys. */
const stack: object[] = [];
let scrollLocks = 0;
let previousOverflow = '';

/**
 * What every modal needs while open: Esc closes it, Tab stays inside it, the
 * page behind does not scroll, and focus returns to where it was on close.
 */
export function useOverlay<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  // Held in a ref so an inline onClose does not re-run the effect, which would
  // move focus back to the top of the dialog on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Read while rendering: by the time an effect runs, a child's React autoFocus
  // has already moved focus into the dialog.
  const returnTo = useMemo(
    () => (open ? (document.activeElement as HTMLElement | null) : null),
    [open],
  );

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    stack.push(token);

    if (scrollLocks === 0) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLocks += 1;

    const node = ref.current;
    if (!node?.contains(document.activeElement)) {
      const autofocus = node?.querySelector<HTMLElement>('[autofocus], [data-autofocus]');
      (autofocus ?? node)?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (stack[stack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      stack.splice(stack.indexOf(token), 1);
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = previousOverflow;
      returnTo?.focus?.();
    };
  }, [open, returnTo]);

  return ref;
}
